import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Wrench, AlertCircle } from 'lucide-react';

interface OpenInspection {
  inspection_id: number;
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
  inspection_status: string;
  current_hours: number;
  inspection_date: string;
  last_updated: string;
  performed_by: string;
  template_name: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

const translations = {
  en: {
    title: 'Open Inspections',
    back: 'Back to Machine Hub',
    noOpenInspections: 'No open inspections',
    noOpenInspectionsMessage: 'All inspections are completed.',
    started: 'Started',
    lastUpdated: 'Last updated',
    by: 'by',
    hours: 'Hours',
    status: 'Status',
    resumeInspection: 'Resume Inspection',
    loading: 'Loading open inspections...',
    draft: 'Draft',
    inProgress: 'In Progress'
  },
  es: {
    title: 'Inspecciones Abiertas',
    back: 'Volver al Centro de Máquinas',
    noOpenInspections: 'No hay inspecciones abiertas',
    noOpenInspectionsMessage: 'Todas las inspecciones están completadas.',
    started: 'Iniciado',
    lastUpdated: 'Última actualización',
    by: 'por',
    hours: 'Horas',
    status: 'Estado',
    resumeInspection: 'Reanudar Inspección',
    loading: 'Cargando inspecciones abiertas...',
    draft: 'Borrador',
    inProgress: 'En Progreso'
  }
};

export const OpenInspections: React.FC = () => {
  const navigate = useNavigate();

  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const [openInspections, setOpenInspections] = useState<OpenInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const t = translations[language];

  // Language change listener
  useEffect(() => {
    const langData = localStorage.getItem('language');
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // Load open inspections
  useEffect(() => {
    loadOpenInspections();
  }, []);

  const loadOpenInspections = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/inspections/open`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load open inspections');
      }

      const data = await response.json();
      setOpenInspections(data.data || []);
    } catch (error) {
      console.error('Error loading open inspections:', error);
      setError('Failed to load open inspections');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeInspection = (equipmentId: number) => {
    navigate(`/mechanics/inspection/${equipmentId}`);
  };

  const handleBackToHub = () => {
    navigate('/mechanics/machine-hub');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return language === 'en' ? 'Just now' : 'Justo ahora';
    if (diffMins < 60) return `${diffMins} ${language === 'en' ? 'min ago' : 'min atrás'}`;
    if (diffHours < 24) return `${diffHours} ${language === 'en' ? 'hr ago' : 'hrs atrás'}`;
    return `${diffDays} ${language === 'en' ? 'days ago' : 'días atrás'}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return '#94a3b8'; // Gray
      case 'in_progress':
        return '#f59e0b'; // Orange
      default:
        return '#64748b'; // Default gray
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return t.draft;
      case 'in_progress':
        return t.inProgress;
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <div style={styles.headerTitle}>{t.title}</div>
          </div>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>{t.loading}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <div style={styles.headerTitle}>{t.title}</div>
          </div>
        </div>
        <div style={styles.errorContainer}>
          <AlertCircle size={48} color="#ef4444" />
          <div style={styles.errorText}>{error}</div>
          <button onClick={loadOpenInspections} style={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerTitle}>{t.title}</div>
        </div>
        <div style={styles.headerSubtitle}>
          {openInspections.length} {openInspections.length === 1 ? 'inspection' : 'inspections'}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {openInspections.length === 0 ? (
          <div style={styles.emptyState}>
            <Wrench size={64} color="#cbd5e1" />
            <div style={styles.emptyStateTitle}>{t.noOpenInspections}</div>
            <div style={styles.emptyStateMessage}>{t.noOpenInspectionsMessage}</div>
            <button onClick={handleBackToHub} style={styles.backToHubButton}>
              {t.back}
            </button>
          </div>
        ) : (
          <div style={styles.inspectionsList}>
            {openInspections.map((inspection) => (
              <div key={inspection.inspection_id} style={styles.inspectionCard}>
                {/* Equipment Info */}
                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.equipmentName}>{inspection.equipment_name}</div>
                    <div style={styles.equipmentNumber}>
                      #{inspection.bradanna_equipment_number}
                    </div>
                  </div>
                  <div
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(inspection.inspection_status)
                    }}
                  >
                    {getStatusLabel(inspection.inspection_status)}
                  </div>
                </div>

                {/* Inspection Details */}
                <div style={styles.cardDetails}>
                  <div style={styles.detailRow}>
                    <div style={styles.detailLabel}>{t.started}:</div>
                    <div style={styles.detailValue}>{formatDate(inspection.inspection_date)}</div>
                  </div>

                  <div style={styles.detailRow}>
                    <div style={styles.detailLabel}>{t.lastUpdated}:</div>
                    <div style={styles.detailValue}>
                      <Clock size={14} style={{ marginRight: '4px' }} />
                      {formatRelativeTime(inspection.last_updated)}
                    </div>
                  </div>

                  <div style={styles.detailRow}>
                    <div style={styles.detailLabel}>{t.by}:</div>
                    <div style={styles.detailValue}>{inspection.performed_by}</div>
                  </div>

                  {inspection.current_hours > 0 && (
                    <div style={styles.detailRow}>
                      <div style={styles.detailLabel}>{t.hours}:</div>
                      <div style={styles.detailValue}>{inspection.current_hours}</div>
                    </div>
                  )}

                  <div style={styles.detailRow}>
                    <div style={styles.detailLabel}>Template:</div>
                    <div style={styles.detailValue}>{inspection.template_name}</div>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handleResumeInspection(inspection.equipment_id)}
                  style={styles.resumeButton}
                >
                  <Wrench size={18} />
                  {t.resumeInspection}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '20px',
    paddingBottom: '40px',
    maxWidth: '800px',
    margin: '0 auto',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    backgroundColor: '#ea580c',
    color: 'white',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '20px'
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: 'white',
    padding: '8px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px'
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '600',
    flex: 1
  },
  headerSubtitle: {
    fontSize: '14px',
    opacity: 0.9
  },
  content: {
    minHeight: '400px'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px'
  },
  loadingText: {
    fontSize: '16px',
    color: '#64748b'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px'
  },
  errorText: {
    fontSize: '16px',
    color: '#ef4444',
    textAlign: 'center'
  },
  retryButton: {
    padding: '10px 24px',
    backgroundColor: '#ea580c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
    padding: '40px 20px'
  },
  emptyStateTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b'
  },
  emptyStateMessage: {
    fontSize: '14px',
    color: '#64748b',
    textAlign: 'center'
  },
  backToHubButton: {
    padding: '12px 32px',
    backgroundColor: '#ea580c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px'
  },
  inspectionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  inspectionCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '2px solid #e2e8f0'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f1f5f9'
  },
  equipmentName: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '4px'
  },
  equipmentNumber: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500'
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px'
  },
  cardDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px'
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px'
  },
  detailLabel: {
    color: '#64748b',
    fontWeight: '500',
    minWidth: '100px'
  },
  detailValue: {
    color: '#1e293b',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center'
  },
  resumeButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#ea580c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  }
};
